import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Snackbar, Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Stack,
} from '@mui/material';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useSalonContextStore } from '../../store/salonContextStore';
import { attendanceApi, AttendanceSession, fmtMinutes } from '../../services/employeeService';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, PersonAvatar, PillTone } from '../../components/ui';
import { formatTime, countLabel } from '../../utils/format';

const SESSION_STATUS: Record<string, { label: string; tone: PillTone }> = {
  IN_PROGRESS: { label: 'W pracy', tone: 'success' },
  ON_BREAK: { label: 'Wyjście', tone: 'warning' },
  COMPLETED: { label: 'Zakończona', tone: 'neutral' },
  NEEDS_REVIEW: { label: 'Do sprawdzenia', tone: 'danger' },
};

export const TodayAttendancePage: React.FC = () => {
  const { activeSalonId } = useSalonContextStore();
  const [rows, setRows] = useState<AttendanceSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!activeSalonId) return;
    setLoading(true);
    try { setRows(await attendanceApi.today(activeSalonId)); } finally { setLoading(false); }
  }, [activeSalonId]);
  useEffect(() => { load(); }, [load]);

  const flagStale = async () => {
    if (!activeSalonId) return;
    const r = await attendanceApi.flagStale(activeSalonId);
    setToast(`Oznaczono ${(r as { flagged: number }).flagged} niezamkniętych zmian`);
    load();
  };

  const atWork = rows.filter((s) => s.status === 'IN_PROGRESS' || s.status === 'ON_BREAK').length;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Zespół' }]}
        title="Dzisiejsza obecność"
        description={rows.length ? `${countLabel(atWork, 'osoba', 'osoby', 'osób')} w pracy teraz · ${countLabel(rows.length, 'zmiana', 'zmiany', 'zmian')} dziś` : 'Kto rozpoczął dziś pracę i ile czasu już przepracował.'}
        actions={<Button variant="outlined" onClick={flagStale}>Wykryj niezamknięte zmiany</Button>}
      />

      {loading ? (
        <LoadingState variant="table" rows={4} />
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<FactCheckOutlinedIcon />}
            title="Nikt jeszcze nie rozpoczął dziś pracy"
            description="Zmiana pojawi się tutaj, gdy pracownik zarejestruje wejście w swoim panelu czasu pracy."
          />
        </SectionCard>
      ) : (
        <SectionCard title="Zmiany dzisiaj" padding="none">
          <TableContainer>
            <Table sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Pracownik</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell align="right">Czas faktyczny</TableCell>
                  <TableCell align="right">Czas rozliczeniowy</TableCell>
                  <TableCell align="right">Wyjścia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((s) => {
                  const st = SESSION_STATUS[s.status] || { label: s.status, tone: 'neutral' as PillTone };
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" gap={1.25}>
                          <PersonAvatar firstName={s.staff?.firstName} lastName={s.staff?.lastName} seed={s.staff?.id} size={32} />
                          <Typography sx={{ fontWeight: 600, fontSize: 'inherit' }}>{s.staff?.firstName} {s.staff?.lastName}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell><StatusPill label={st.label} tone={st.tone} /></TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatTime(s.startedAt)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(s.actualWorkMinutes)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(s.billableWorkMinutes)}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMinutes(s.actualBreakMinutes)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionCard>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TodayAttendancePage;
